/**
 * MeetGraphic — a generated, dependency-free SVG "poster" for a discovered
 * meet. No external image calls: the art is derived from the meet's format
 * (and a stable hash of its id) so each card gets a colorful, on-theme visual
 * that fills the left panel. Keeps the app fast and free.
 */

type Theme = {
  from: string;
  to: string;
  accent: string;
  /** emoji-free inline icon drawn as SVG paths */
  icon: "bolt" | "shields" | "bracket" | "mat" | "whistle";
  label: string;
};

/** Map a format string to a color theme + icon. */
function themeFor(format?: string): Theme {
  const f = (format ?? "").toLowerCase();
  if (f.includes("war")) {
    return { from: "#f0603a", to: "#b11e3a", accent: "#ff7a4d", icon: "bolt", label: "WAR ZONE" };
  }
  if (f.includes("dual")) {
    return { from: "#3b82f6", to: "#1e3a8a", accent: "#60a5fa", icon: "shields", label: "DUALS" };
  }
  if (f.includes("open")) {
    return { from: "#10b981", to: "#065f46", accent: "#34d399", icon: "bracket", label: "OPEN" };
  }
  if (f.includes("folk")) {
    return { from: "#8b5cf6", to: "#5b21b6", accent: "#a78bfa", icon: "mat", label: "FOLKSTYLE" };
  }
  // Default (generic tournament)
  return { from: "#14b8a6", to: "#0f766e", accent: "#2dd4bf", icon: "whistle", label: "MEET" };
}

/** Deterministic 0..1 from a string (FNV-1a) for subtle per-card variation. */
function hash01(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) / 0xffffffff;
}

function IconPaths({ icon }: { icon: Theme["icon"] }) {
  const stroke = "rgba(255,255,255,0.92)";
  switch (icon) {
    case "bolt":
      return (
        <path
          d="M34 8 L18 34 L30 34 L26 52 L46 24 L33 24 Z"
          fill={stroke}
          opacity="0.95"
        />
      );
    case "shields":
      return (
        <g fill="none" stroke={stroke} strokeWidth="3.2">
          <path d="M22 12 L22 26 C22 34 16 38 16 38 C16 38 10 34 10 26 L10 12 Z" fill="rgba(255,255,255,0.18)" />
          <path d="M50 22 L50 36 C50 44 44 48 44 48 C44 48 38 44 38 36 L38 22 Z" fill="rgba(255,255,255,0.18)" />
        </g>
      );
    case "bracket":
      return (
        <g fill="none" stroke={stroke} strokeWidth="3" strokeLinecap="round">
          <path d="M14 14 H26 V30 H38" />
          <path d="M14 46 H26 V30" />
          <path d="M38 30 H50" />
          <circle cx="52" cy="30" r="3.2" fill={stroke} stroke="none" />
        </g>
      );
    case "mat":
      return (
        <g fill="none" stroke={stroke} strokeWidth="3">
          <rect x="12" y="12" width="40" height="40" rx="4" />
          <circle cx="32" cy="32" r="11" />
          <circle cx="32" cy="32" r="3.2" fill={stroke} stroke="none" />
        </g>
      );
    case "whistle":
    default:
      return (
        <g fill="none" stroke={stroke} strokeWidth="3" strokeLinecap="round">
          <path d="M16 26 H40 a10 10 0 1 1 -10 10 V26" fill="rgba(255,255,255,0.15)" />
          <path d="M40 22 L48 16" />
          <circle cx="30" cy="36" r="4" fill={stroke} stroke="none" />
        </g>
      );
  }
}

export function MeetGraphic({
  format,
  seed,
  difficulty,
}: {
  format?: string;
  seed: string;
  difficulty?: number;
}) {
  const t = themeFor(format);
  const angle = Math.round(90 + hash01(seed) * 60); // 90–150deg gradient tilt
  const gid = `g-${seed}`;
  return (
    <svg
      className="meet-graphic"
      viewBox="0 0 120 160"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-label={`${t.label} event graphic`}
    >
      <defs>
        <linearGradient id={gid} gradientTransform={`rotate(${angle} 0.5 0.5)`}>
          <stop offset="0%" stopColor={t.from} />
          <stop offset="100%" stopColor={t.to} />
        </linearGradient>
      </defs>
      <rect width="120" height="160" fill={`url(#${gid})`} />
      {/* subtle diagonal sheen */}
      <polygon points="0,0 120,0 0,110" fill="rgba(255,255,255,0.07)" />
      {/* centered themed icon */}
      <g transform="translate(28, 44) scale(1.0)">
        <IconPaths icon={t.icon} />
      </g>
      {/* format label */}
      <text
        x="60"
        y="128"
        textAnchor="middle"
        fontSize="13"
        fontWeight="700"
        fill="rgba(255,255,255,0.95)"
        letterSpacing="1"
      >
        {t.label}
      </text>
      {/* difficulty pips */}
      {typeof difficulty === "number" && (
        <g transform="translate(36,140)">
          {[0, 1, 2, 3, 4].map((i) => (
            <circle
              key={i}
              cx={i * 12}
              cy={0}
              r={3}
              fill={i < difficulty ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.3)"}
            />
          ))}
        </g>
      )}
    </svg>
  );
}

/** Expose the accent color for a format so cards can tint borders/badges. */
export function accentFor(format?: string): string {
  return themeFor(format).accent;
}
